import { Component, Inject, Input, OnInit, Optional, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, FormArray, AbstractControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SelectButton } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';

import { CalculationRecordItem, DisplayTypeDefinitions } from 'src/app/models/calculationStepsModel';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { ActionButtonEvent, ColumnType, InputType, TableColumnModel } from 'src/app/templates/table-template/tableModel';
import { TableTemplateComponent } from 'src/app/templates/table-template/table-template.component';

@Component({
  selector: 'app-cost-calculator',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, TableTemplateComponent,
    SelectButton, ButtonModule, MatInputModule, MatSelectModule],
  templateUrl: './cost-calculator.component.html',
  styleUrl: './cost-calculator.component.scss'
})
export class CostCalculatorComponent implements OnInit {
  @ViewChild(TableTemplateComponent) tableComponent!: TableTemplateComponent;
  @Input() inputPayload: any;
  @Input() standaloneMode = false;
  isViewMode = false;
  costCalculatorForm !: FormGroup;
  costCalculationSteps: any[];
  isShopMode = true;
  isShopModeToggle = "true";
  shopToggleStateOptions: any[] = [{ label: 'Shop', value: 'true' }, { label: 'Line', value: 'false' }];
  loader = { isLoading: true, message: 'Loading Cost Calculation Steps...' };
  errorMessage = '';
  title = "";
  primaryCompany: any = {};
  primaryPlant: any = {};
  primaryShop: any = {};
  plantShops: { [key: string]: any[] } = {};
  shopConfiguration: { [key: string]: any };
  primaryShopConfig: any;
  aggregatedDownTimeCosts: { [key: string]: any };
  lineConfiguration: { [key: string]: any };
  lineTableData: any[] = [];
  totalLineDowntime = 0;
  totalLineDowntimeCost = 0;
  //Line map object to store line id as key and previous line_id as value
  lineMap: { [key: string]: string } = {};
  lineColumnList: TableColumnModel[] = [
    { name: "line_id", displayField: "line_name", label: "Line Name", type: ColumnType.text,
      inputType: InputType.select, inputPlaceholder: 'Select Line', required: true },
    { name: "downtime_minutes", label: "Downtime (Minutes)", type: ColumnType.numeric,
      inputType: InputType.numeric, required: true, greaterThanValue: 0 },
    { name: "downtime_cost", label: "Downtime Cost", type: ColumnType.numeric, currency: 'USD' }
  ];
  entryFiscalYear = 0;
  isVehicleAvoidanceAllowed = false;
  
  constructor(
    @Optional() private readonly dialogRef: MatDialogRef<CostCalculatorComponent>,
    @Inject(MAT_DIALOG_DATA) @Optional() public payload: any, private readonly formBuilder: FormBuilder,
    private readonly eventService: MfgappsEventService, private readonly mfgappsApiService: MfgappsApiService) { }

  ngOnInit() {
    this.payload = Object.freeze(this.payload ?? this.inputPayload);
    this.initializeCalculatorData();
  }

  async initializeCalculatorData() {
    this.title = this.payload.title;
    this.isViewMode = this.payload?.isViewMode;
    const companyList = await this.eventService.getCorporateHierarchy();
    this.costCalculationSteps = this.payload.costCalculationSteps;
    const entryDetails = this.payload.entryDetails;
    this.entryFiscalYear = this.eventService.getFiscalYearFromDate(entryDetails.entryDate ?? entryDetails.date);
    this.primaryCompany = companyList.find(company => company.company_id === entryDetails.company_id);
    this.primaryPlant = this.primaryCompany?.plantList.find(plant => plant.plant_id === entryDetails.plant_id);
    this.primaryShop = this.primaryPlant?.shopList.find(shop => shop.shop_id === entryDetails.shop_id);
    this.primaryCompany.plantList.forEach(plant => this.plantShops[plant.plant_id] = plant.shopList);
    this.isVehicleAvoidanceAllowed = this.eventService.checkIfShopHasVehicleAvoidance(this.primaryShop);
    await this.initializeFormValidators();
  }

  async switchShopMode() {
    const previousToggleState = this.isShopMode;
    let revertToggle = false;
    if (!this.lineConfiguration) {
      await this.loadLineConfiguration();
    }
    // MRH-9273 Show only line items with Downtime cost > 0 and show warning if no line items have DT > 0
    if (this.isShopMode && this.lineColumnList[0].inputSelectOptions.length === 0) {
      const info = Object.values(this.lineConfiguration).length > 0 ? 'have Downtime Cost > 0' : 'are configured';
      const warningMsg = `No Line items ${info} for this Shop ! Please continue in Shop mode to add Downtime Costs`;
      await this.eventService.confirmDialog(warningMsg, true);
      revertToggle = true;
    }
    //Show dialog confirmation to user before switching shop or line mode
    if (!revertToggle && await this.eventService.confirmDialog('Previous configuration changes will be lost. Do you wish to proceed?')) {
      this.isShopMode = !this.isShopMode;
      this.isShopModeToggle = JSON.stringify(this.isShopMode);
      this.costCalculatorForm?.reset();
      await this.initializeFormValidators(true);
    }
    else {
      //Revert to previous toggle state without any field changes
      this.isShopModeToggle = JSON.stringify(previousToggleState);
    }
  }

  async initializeFormValidators(toggleByUser = false) {
    //Load shop config only first time when null
    if (!this.shopConfiguration) {
      await this.loadShopConfiguration();
    }
    if (!toggleByUser && this.costCalculationSteps?.some(each => each.display_type.startsWith('L'))) {
      this.isShopMode = false;
      this.isShopModeToggle = JSON.stringify(this.isShopMode);
    }
    if (!this.isShopMode) {
      await this.initializeLineData();
    }
    if (!this.shopConfiguration?.[this.primaryShop.shop_id]) {
      return;
    }
    this.primaryShopConfig = this.shopConfiguration[this.primaryShop.shop_id];
    this.costCalculatorForm = this.formBuilder.group({
      items: this.formBuilder.array([]),
      totalDownTimeCost: [{ value: 0, disabled: true }]
    });
    if (this.isVehicleAvoidanceAllowed) {
      this.costCalculatorForm.addControl('totalVehicleAvoidance', this.formBuilder.control({ value: 0, disabled: true }));
    }
    //Initialize default form groups if calculation steps have never been added
    if (!this.costCalculationSteps) {
      this.addNewFormGroup('P1'); //Primary Shop
      this.addNewFormGroup('S1'); //Additional Shop 1
      this.addNewFormGroup('S2'); //Additional Shop 2
      this.addNewFormGroup('M1'); //Miscellaneous Cost
    }
    else {
      this.eventService.sortCalcStepsByDisplayType(this.costCalculationSteps);
      for (const calculationStep of this.costCalculationSteps) {
        //In view mode, only load those calculation steps having downtime or additional cost
        if (!calculationStep.display_type.startsWith('L') &&
          (!this.isViewMode || calculationStep.downtime_minutes || calculationStep.additional_cost)) {
          this.addNewFormGroup(calculationStep.display_type, calculationStep);
        }
      }
      this.calculateDownTimeCosts();
    }
  }

  get items(): FormArray {
    return this.costCalculatorForm.get('items') as FormArray;
  }

  control(i: number, controlName: string) {
    return (this.items.at(i) as FormGroup).get(controlName);
  }

  addNewFormGroup(displayType: string, existingRecord?: any) {
    const isPrimary = DisplayTypeDefinitions[displayType].isPrimary;
    const labelPrefix = displayType.startsWith('CI') ? `${this.primaryCompany.company_code} ` : '';
    const isPlantImpactShop = this.primaryShopConfig.plant_impact_minutes > 0 && this.primaryShopConfig.company_impact_minutes === 0;
    const formGroupData: CalculationRecordItem = {
      label: labelPrefix + DisplayTypeDefinitions[displayType].label,
      display_type: displayType,
      company_id: this.primaryCompany.company_id,
      plant_id: [{
        value: existingRecord?.plant_id ?? ((isPrimary || isPlantImpactShop) ? this.primaryPlant.plant_id : null),
        disabled: (isPrimary || isPlantImpactShop || displayType.includes('I') || this.isViewMode)
      }],
      shop_id: [{
        value: existingRecord?.shop_id ?? ((isPrimary || displayType === 'M1') ? this.primaryShop.shop_id : null),
        disabled: (isPrimary || displayType.includes('I') || displayType === 'M1' || this.isViewMode)
      }, [this.eventService.checkUniqueValidator('shop_id', this.costCalculatorForm),
        this.eventService.combinedFieldValidator('plant_id', true)]],
      downtime_minutes: [{ value: existingRecord?.downtime_minutes ?? 0, disabled: true }],
      //Disable actual downtime minutes if 1) primary shop in line mode 2) miscellaneous
      // 3) when additional shop is not selected 4) impact type 5) read-only mode (view entry)
      actual_downtime_minutes: [{ value: existingRecord?.actual_downtime_minutes ?? 0,
        disabled: (displayType === 'M1' || this.isViewMode
          || (isPrimary && !this.isShopMode) || (!isPrimary && !existingRecord?.shop_id))},
          [...(isPrimary ? [Validators.required] : []),
          ...[this.eventService.combinedFieldValidator('shop_id', true), Validators.min(0.01)]]],
      downtime_cost: [{ value: 0, disabled: true }],
      additional_cost: [{ value: existingRecord?.additional_cost, disabled: this.isViewMode }, 
        [this.eventService.combinedFieldValidator('description', true)]],
      description: [{ value: existingRecord?.description?.trim(), disabled: this.isViewMode }, 
        [this.eventService.combinedFieldValidator('additional_cost')]]
    };
    this.items.push(this.formBuilder.group(formGroupData));
  }

  isShopConfigurationMissing(formData: any) {
    for (const shopDowntimeRow of formData.items) {
      if (shopDowntimeRow.display_type !== 'M1' && shopDowntimeRow.shop_id) {
        if (!this.shopConfiguration[shopDowntimeRow.shop_id]) {
          const shopItem = this.plantShops[shopDowntimeRow.plant_id]?.find(shop => shop.shop_id === shopDowntimeRow.shop_id);
          return shopItem?.shop_name ?? shopDowntimeRow.shop_id;
        }
      }
    }
    return '';
  }

  clearImpactFields() {
    //Loop in reverse order so that removing items does not affect the index of yet to be checked items
    for (let i = this.items.getRawValue().length - 1; i >= 0; i--) {
      if (this.items.at(i).get('display_type').value.includes('I')) {
        this.items.removeAt(i);
      }
    }
  }

  calculateDownTimeCosts(updatedGroup: AbstractControl = null, selectionChange = '') {
    if (updatedGroup) {
      //Remove all existing impact group fields whenever user changes fields
      this.clearImpactFields();
      //Reset Shop field values when shop is deselected or plant is changed (set shop_id null)
      if (!updatedGroup.get('shop_id').value || selectionChange === 'plant') {
        updatedGroup.patchValue({ downtime_cost: 0, downtime_minutes: 0, actual_downtime_minutes: 0, shop_id: null });
        updatedGroup.get('actual_downtime_minutes').disable();
      }
      else {
        updatedGroup.get('actual_downtime_minutes').enable();
      }
    }
    this.costCalculatorForm.markAllAsTouched();
    const missingShop = this.isShopConfigurationMissing(this.costCalculatorForm.getRawValue());
    if (missingShop) {
      this.eventService.showSnackBar(`Missing Cost Configuration for Shop: ${missingShop}`, 'error');
      return;
    }
    for (const group of this.items.controls as FormGroup[]) {
      const groupValues = group.getRawValue();
      if (groupValues.shop_id && groupValues.display_type !== 'M1' && groupValues.actual_downtime_minutes >= 0) {
        //Update downtime_minutes with actual downtime minutes first before calculating costs and impact groups
        group.get('downtime_minutes').setValue(groupValues.actual_downtime_minutes);
        this.setEachShopDownTimeCost(group);
      }
    }
    //sort all downtime cost field rows by standard order after calculations
    this.eventService.sortCalcStepsByDisplayType(this.items.controls);
    this.setTotalDownTimeCost(updatedGroup);
  }

  setTotalDownTimeCost(updatedGroup: AbstractControl = null) {
    //Re-validate all controls in the updated group to trigger validator messages
    for (const control in (updatedGroup as FormGroup)?.controls) {
      updatedGroup.get(control).updateValueAndValidity();
    }
    let totalDownTimeCost = 0;
    for (const downtimeRow of this.costCalculatorForm.getRawValue().items) {
      if (downtimeRow.display_type === 'M1') {
        totalDownTimeCost += downtimeRow.additional_cost ?? 0;
      }
      else {
        totalDownTimeCost += downtimeRow.downtime_cost;
      }
    }
    this.costCalculatorForm.get('totalDownTimeCost').setValue(Math.round(totalDownTimeCost * 100) / 100);
  }

  setEachShopDownTimeCost(group: FormGroup) {
    const groupValues = group.getRawValue();
    let downtimeCost = 0;
    let excessMinutes = 0;
    const shopConfig = this.shopConfiguration[groupValues.shop_id];
    if (!this.isShopMode && groupValues.display_type === 'P1') {
      //In line mode, downtime time and cost of primary shop are set as total line downtime and cost
      group.patchValue({ downtime_minutes: this.totalLineDowntime, actual_downtime_minutes: this.totalLineDowntime });
      downtimeCost = this.totalLineDowntimeCost;
    }
    else if (shopConfig.plant_impact_minutes > 0) {
      if (groupValues.downtime_minutes <= shopConfig.plant_impact_minutes) {
        downtimeCost = groupValues.downtime_minutes * shopConfig.downtime_cost;
      }
      else {
        excessMinutes = groupValues.downtime_minutes - shopConfig.plant_impact_minutes;
        group.get('downtime_minutes').setValue(shopConfig.plant_impact_minutes);
        downtimeCost = shopConfig.plant_impact_minutes * shopConfig.downtime_cost;
      }
      this.addOrUpdateImpactGroup(group, excessMinutes, "PI");
    }
    else if (shopConfig.company_impact_minutes > 0) {
      if (groupValues.downtime_minutes <= shopConfig.company_impact_minutes) {
        downtimeCost = groupValues.downtime_minutes * shopConfig.downtime_cost;
      }
      else {
        excessMinutes = groupValues.downtime_minutes - shopConfig.company_impact_minutes;
        group.get('downtime_minutes').setValue(shopConfig.company_impact_minutes);
        downtimeCost = shopConfig.company_impact_minutes * shopConfig.downtime_cost;
      }
      this.addOrUpdateImpactGroup(group, excessMinutes, "CI");
    }
    else {
      downtimeCost = groupValues.downtime_minutes * shopConfig.downtime_cost;
    }
    if (this.isVehicleAvoidanceAllowed && this.primaryShop.shop_id === groupValues.shop_id && 'takt_time_seconds' in shopConfig) {
      const vehicleAvoidance = shopConfig.takt_time_seconds !== 0 ? ((group.get('downtime_minutes').value * 60) / shopConfig.takt_time_seconds) : 0;
      this.costCalculatorForm.get('totalVehicleAvoidance').setValue(Math.round(vehicleAvoidance));
    }
    group.get('downtime_cost')?.setValue(Math.round(downtimeCost * 100) / 100);
  }

  addOrUpdateImpactGroup(rootGroup: any, excessMinutes: number, displayTypePrefix: 'PI' | 'CI') {
    const formValues: any[] = this.items.getRawValue();
    const rootGroupValues: any = rootGroup.getRawValue();
    const impactGroups = formValues.filter(each => each.display_type.startsWith(displayTypePrefix));
    if (impactGroups.length === 0 && excessMinutes === 0) {
      return;
    }
    let existingImpactGroup: any;
    const labelPrefix = displayTypePrefix === 'CI' ? `${this.primaryCompany.company_code} ` : '';
    if (displayTypePrefix === 'PI') {
      existingImpactGroup = formValues.find(each => each.display_type.startsWith(displayTypePrefix)
        && each.plant_id === rootGroupValues.plant_id);
    }
    else if (displayTypePrefix === 'CI') {
      existingImpactGroup = formValues.find(each => each.display_type.startsWith(displayTypePrefix)
        && each.company_id === rootGroupValues.company_id);
    }
    const displayType = displayTypePrefix + (impactGroups.length + 1);
    const impactGroup = {
      label: existingImpactGroup?.label ?? labelPrefix + DisplayTypeDefinitions[displayType].label,
      display_type: existingImpactGroup?.display_type ?? displayType,
      company_id: rootGroupValues.company_id,
      plant_id: [{ value: existingImpactGroup?.plant_id ?? (displayTypePrefix === 'PI' ? rootGroupValues.plant_id : null), disabled: true }],
      shop_id: [{ value: null, disabled: true }],
      downtime_minutes: [{ value: 0, disabled: true }],
      actual_downtime_minutes: [{ value: 0, disabled: true }],
      downtime_cost: [{ value: 0, disabled: true }],
      contributions: { ...(existingImpactGroup ? existingImpactGroup['contributions'] : {}), 
        ...{ [rootGroupValues.display_type]: excessMinutes } }
    };
    for (const value of Object.values(impactGroup.contributions) as number[]) {
      impactGroup.downtime_minutes[0].value += value;
    }
    if (displayTypePrefix.startsWith('PI')) {
      impactGroup.downtime_cost[0].value = Math.round(impactGroup.downtime_minutes[0].value * this.aggregatedDownTimeCosts[impactGroup.plant_id[0].value] * 100) / 100;
    }
    else if (displayTypePrefix.startsWith('CI')) {
      impactGroup.downtime_cost[0].value = Math.round(impactGroup.downtime_minutes[0].value * this.aggregatedDownTimeCosts[impactGroup.company_id] * 100) / 100;
    }
    if (existingImpactGroup) {
      const impactGroupIndex = formValues.findIndex(each => each.display_type === existingImpactGroup.display_type);
      if (impactGroup.downtime_minutes[0].value > 0) {
        this.items.setControl(impactGroupIndex, this.formBuilder.group(impactGroup));
      }
      else {
        this.items.removeAt(impactGroupIndex);
      }
    }
    else if (impactGroup.downtime_minutes[0].value > 0) {
      this.items.push(this.formBuilder.group(impactGroup));
    }
  }

  async onSubmit() {
    this.costCalculatorForm.markAllAsTouched();
    if (this.costCalculatorForm.invalid) {
      this.eventService.showSnackBar('Please clear the validation errors !!', 'error');
      return;
    }
    //Line mode toggle validations
    if (!this.isShopMode) {
      if (this.tableComponent?.isEditMode) {
        this.eventService.showSnackBar('A Line item is being edited, please exit the edit mode !!', 'warn');
        return;
      }
      if (this.lineTableData.length === 0) {
        this.eventService.showSnackBar('No Line item available, please add at least 1 Line downtime item !!', 'warn');
        return;
      }
      if (await this.showLineDowntimeWarning(this.totalLineDowntime)) {
        return;
      }
    }
    const missingShop = this.isShopConfigurationMissing(this.costCalculatorForm.getRawValue());
    if (missingShop) {
      this.eventService.confirmDialog(`Missing Cost Configuration for Shop: ${missingShop}`, true, 'Error');
      return;
    }
    //Show dialog confirmation to user before submitting to parent entry
    if (!this.isViewMode && await this.eventService.confirmDialog(
      'All previous calculation data will be replaced with current data. Do you wish to proceed?')) {
      const formValues = this.costCalculatorForm.getRawValue();
      const lineItems = [];
      if (!this.isShopMode) {
        const primaryShop = this.items.controls.find(group => group.get('display_type').value === 'P1');
        for (const [index, line] of this.lineTableData.entries()) {
          const lineItem = structuredClone(primaryShop.getRawValue());
          lineItem.label = `Line Item ${index + 1}`;
          lineItem.display_type = `L${index + 1}`;
          lineItem.line_id = line.line_id;
          lineItem.actual_downtime_minutes = lineItem.downtime_minutes = line.downtime_minutes;
          lineItems.push(lineItem);
        }
      }
      this.dialogRef.close({
        totalVehicleAvoidance: formValues.totalVehicleAvoidance,
        totalCostAvoidance: formValues.totalDownTimeCost,
        calculationSteps: formValues.items.concat(lineItems)
      });
    }
  }

  async loadShopConfiguration() {
    try {
      this.loader.isLoading = true;
      this.loader.message = `Loading Shop Configuration...`;
      const shopItems: any[] = (await this.mfgappsApiService.getShopConfiguration(this.entryFiscalYear, this.primaryCompany.company_id)).result;
      if (!Array.isArray(shopItems) || shopItems.length === 0) {
        this.errorMessage = 'Shop configuration not found !';
        this.resetLoader();
        return;
      }
      this.shopConfiguration = {};
      this.aggregatedDownTimeCosts = this.eventService.calculateAggregatedDowntimeCosts(shopItems, this.shopConfiguration);
      Object.freeze(this.shopConfiguration);
      Object.freeze(this.aggregatedDownTimeCosts);
      this.resetLoader();
    }
    catch (error) {
      this.handleApiError(error, 'Shop configuration');
    }
  }

  handleApiError(error: any, context: string) {
    console.info(error);
    this.resetLoader();
    this.errorMessage = `Error occurred while loading ${context}: ${error.message}, Please try again !`;
    this.eventService.showSnackBar(this.errorMessage, 'error');
  }

  async loadLineConfiguration() {
    try {
      this.loader.isLoading = true;
      this.loader.message = `Loading Line Configuration...`;
      const lineItems: any[] = (await this.mfgappsApiService.getLineConfiguration(this.entryFiscalYear, this.primaryShop.shop_id)).result;
      if (!Array.isArray(lineItems)) {
        this.errorMessage = 'Line configuration not found !';
        this.resetLoader();
        return;
      }
      this.lineConfiguration = {};
      this.lineColumnList[0].inputSelectOptions = [];
      for (const line of lineItems) {
        this.lineConfiguration[line.line_id] = line;
        // MRH-9273 Show only line items with Downtime cost > 0 in Line Name dropdown in line table
        if (line.downtime_cost > 0) {
          this.lineColumnList[0].inputSelectOptions.push(structuredClone(line));
        }
      }
      Object.freeze(this.lineConfiguration);
      this.resetLoader();
    }
    catch (error) {
      this.handleApiError(error, 'Line configuration');
    }
  }

  async initializeLineData() {
    this.lineMap = {};
    this.lineTableData = [];
    //Load line config only first time when null
    if (!this.lineConfiguration) {
      await this.loadLineConfiguration();
    }
    //Line type downtime records start with 'L' like L1, L2 in display_type
    for (const line of this.costCalculationSteps ?? []) {
      const lineConfigItem = this.lineConfiguration?.[line.line_id];
      if (line.display_type.startsWith('L') && lineConfigItem) {
        this.lineTableData.push({
          id: line.line_id, line_name: lineConfigItem.line_name, downtime_minutes: line.downtime_minutes,
          line_id: line.line_id, downtime_cost: lineConfigItem.downtime_cost * line.downtime_minutes
        });
        this.lineMap[line.line_id] = line.line_id;
      }
    }
    this.updatePrimaryShopFromLines();
  }

  addNewLineItem() {
    if (this.tableComponent && !this.tableComponent.isEditMode) {
      const newLine = { id: 'new', line_name: null, downtime: 0, downtime_cost: 0 };
      this.lineTableData.push(newLine);
      this.tableComponent.initRowEdit(newLine, 'last');
    }
  }

  async handleLineAction(event: ActionButtonEvent) {
    const line = event.entry;
    if (event.action === 'save') {
      const lineConfigItem = this.lineConfiguration[line.line_id];
      //Update line_name based on line_id
      line.line_name = lineConfigItem.line_name;
      //Delete previous line reference from lineMap and add new line reference
      delete this.lineMap[line.id];
      line.id = this.lineMap[line.line_id] = line.line_id;
      //Multiply downtime minutes by line config cost to get line downtime cost
      line.downtime_cost = line.downtime_minutes * lineConfigItem.downtime_cost;
      this.updatePrimaryShopFromLines(true);
    }
    else if (event.action === 'cancel') {
      //If edit is canceled on adding new row, remove the last row
      if (line.id === 'new') {
        this.lineTableData.pop();
      }
    }
    else if (event.action === 'delete') {
      const index = this.lineTableData.findIndex(item => item.id === line.id);
      if (index >= 0 && await this.eventService.confirmDialog(`Do you want to delete line '${line.line_name}' downtime entry?`)) {
        this.lineTableData.splice(index, 1);
        delete this.lineMap[line.id];
        this.updatePrimaryShopFromLines(true);
      }
    }
  }

  async validateLine(line: any) {
    const lineConfigItem = this.lineConfiguration[line.line_id];
    //Check if lineMap has key line_id for new line or existing line changed to another existing line
    if (this.lineMap[line.line_id] && line.line_id !== line.id) {
      await this.eventService.confirmDialog(`Line '${lineConfigItem.line_name}' already exists, please select a different line.`, true);
      return { isValid: false, message: '' };
    }
    let totalLineDowntime = 0;
    this.lineTableData.forEach(line => totalLineDowntime += line.downtime_minutes);
    if (await this.showLineDowntimeWarning(totalLineDowntime)) {
      return { isValid: false, message: ''}
    }
    return { isValid: true, message: '' };
  }

  //Show warning and return if total line downtime exceeds primary shop's plant/company impact minutes
  async showLineDowntimeWarning(totalLineDowntime: number) {
    if ((this.primaryShopConfig.plant_impact_minutes > 0 && totalLineDowntime > this.primaryShopConfig.plant_impact_minutes)
      || (this.primaryShopConfig.company_impact_minutes > 0 && totalLineDowntime > this.primaryShopConfig.company_impact_minutes)) {
      const isPlantImpact = this.primaryShopConfig.plant_impact_minutes > 0;
      const impactText = `Total Line Downtime minutes '${totalLineDowntime}' exceeds ${isPlantImpact ? 'Plant' : 'Company'} Impact minutes`;
      const impactNumber = `${isPlantImpact ? this.primaryShopConfig.plant_impact_minutes : this.primaryShopConfig.company_impact_minutes}`;
      await this.eventService.confirmDialog(`${impactText} '${impactNumber}' of the Primary Shop '${this.primaryShopConfig.shop_name}'.
      Please adjust downtime of the current line or other lines, or switch to Shop mode to enter a greater downtime`, true);
      return true;
    }
    return false;
  }

  updatePrimaryShopFromLines(updateFields = false) {
    //sum all line downtimes to get total line downtime whenever line item is saved or deleted
    this.totalLineDowntime = this.totalLineDowntimeCost = 0;
    for (const line of this.lineTableData) {
      this.totalLineDowntime += line.downtime_minutes;
      this.totalLineDowntimeCost += line.downtime_cost;
    }
    if (updateFields) {
      //Re-calculate primary shop downtime fields whenever user add/update/delete line items
      this.calculateDownTimeCosts();
    }
  }
  
  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  resetError() {
    this.errorMessage = '';
  }

  async cancel() {
    //show confirmation dialog to user only if there are unsaved changes, if pristine then close without asking
    if (!this.isViewMode && this.costCalculatorForm.pristine || (this.costCalculatorForm.dirty 
      && await this.eventService.confirmDialog('Any unsaved data will be lost if you cancel. Do you wish to proceed?'))) {
      this.dialogRef.close();
    }
  }
}